/* eslint-disable react/jsx-no-constructed-context-values */
import {
  Context,
  createContext,
  useEffect,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from "react"
import {
  SheetRow,
  type MappingSchemaJSON,
  type MappingSchemaJSONColumn,
  type MappingSchemaRow,
  type SeriesCombination,
} from "interfaces/dataInterfaces"
import {
  useGetMSByIdQuery,
  useGetMSExcelAsJsonQuery,
  useGetMSSingleRowQuery,
  useUpdateMSJsonSchemaMutation,
} from "services/marketprices/mappingSchemas"
import { buildPartialDate, getDataType, jsonToRowData } from "utils/mappingSchema"
import { useGetSeriesCombinationQuery } from "services/core/series"
import type { InvalidValues } from "interfaces/mappingInterfaces"
import useCommon from "hooks/useCommon"

type PricesContext = {
  sheetData: SheetRow[] | null
  discardedCols: string[]
  mappingRows: MappingSchemaRow[]
  errorModal: { show: boolean; invalidValues: InvalidValues[] }
  schema: MappingSchemaJSON
  adm0Code: number
  fileName: string
  seriesCombinations: SeriesCombination[]
  setDiscardedCols: Dispatch<SetStateAction<string[]>>
  loadingMapping: boolean
  resetErrorModal: () => void
  onGlobalEdit: (key: string, data: string) => Promise<void>
  onUpdateRow: (row: MappingSchemaRow) => Promise<void>
  onDeleteRow: (row: MappingSchemaRow) => Promise<void>
  onDiscardRow: (row: MappingSchemaRow) => Promise<void>
  onRestoreRow: (colName: string) => Promise<void>
  editMappingRow: (row: MappingSchemaRow) => void
}

const initialErrorModal: { show: boolean; invalidValues: InvalidValues[] } = { show: false, invalidValues: [] }
const initialColumn: MappingSchemaJSONColumn = {
  commodity: "",
  currency: "",
  dataSource: "",
  date: "",
  market: "",
  price: "",
  priceType: "",
  unit: "",
}
const initialSchema: MappingSchemaJSON = {
  templateId: "",
  frequency: "",
  dataSource: "",
  dateFormat: "",
  currency: "",
  priceType: "",
  rowHeader: "",
  headerHash: "",
  lastUpdateDate: "",
  discardedRows: [],
  metadataFields: [],
  columns: [initialColumn],
}

export const MappingPriceContext: Context<PricesContext> = createContext<PricesContext>({
  sheetData: [],
  discardedCols: [],
  mappingRows: [],
  errorModal: initialErrorModal,
  schema: initialSchema,
  adm0Code: 0,
  fileName: "",
  seriesCombinations: [],
  loadingMapping: false,
  setDiscardedCols: () => {},
  editMappingRow: () => {},
  resetErrorModal: () => {},
  onGlobalEdit: async () => {},
  onUpdateRow: async () => {},
  onDeleteRow: async () => {},
  onDiscardRow: async () => {},
  onRestoreRow: async () => {},
})

const MappingPriceProvider = ({ id, children }: { id: string; children: ReactElement }): ReactElement => {
  const { showSnackBar } = useCommon()
  const [headerRow, setHeaderRow] = useState<string>("0")
  const [sheetData, setSheetData] = useState<SheetRow[] | null>(null)
  const [discardedCols, setDiscardedCols] = useState<string[]>([])

  const [mappingRows, setMappingRows] = useState<MappingSchemaRow[]>([])
  const [errorModal, setErrorModal] = useState<{ show: boolean; invalidValues: InvalidValues[] }>(initialErrorModal)
  // const [combinations, setCombinations] = useState<{
  //   commodity: SeriesCombination[]
  //   market: SeriesCombination[]
  //   currency: SeriesCombination[]
  // }>({
  //   commodity: [],
  //   market: [],
  //   currency: [],
  // })
  const [schema, setSchema] = useState<MappingSchemaJSON>(initialSchema)

  const {
    data: msData,
    refetch: refetchMsData,
    isFetching: loadingMsData,
  } = useGetMSByIdQuery(Number(id), { skip: !id })
  const fileName = msData?.mappingSchemaFileUsed ?? ""

  const { data: singleRow, isFetching: loadingSingleRow } = useGetMSSingleRowQuery(
    { rowHeaderPosition: Number(headerRow), fileName },
    { skip: !fileName || !Number(headerRow) },
  )
  const { data: excelJson, isFetching: loadingExcel } = useGetMSExcelAsJsonQuery(
    { fileName, msId: Number(id) },
    { skip: !id || !fileName },
  )
  const { data: seriesCombinations } = useGetSeriesCombinationQuery(
    { admCode: msData?.adm0Code ?? 0, skip: 0, take: 0 },
    { skip: !msData },
  )

  const [updateJsonSchema] = useUpdateMSJsonSchemaMutation()

  const resetErrorModal = (): void => setErrorModal(initialErrorModal)

  const updateMappingRow = (row: MappingSchemaRow): void => {
    setMappingRows(
      mappingRows.map(item =>
        item.colName === row.colName
          ? { ...row, type: "", dataType: "", commodity: "", unit: "", value: "", metadata: "" }
          : item,
      ),
    )
  }

  // const handleValidation = (row: MappingSchemaRow): boolean => {
  //   if (!excelJson || !row || !combinations) return false
  //
  //   const result = excelValidation(excelJson, row, Number(headerRow), combinations)
  //
  //   if (result.length) {
  //     setErrorModal({ show: true, invalidValues: result })
  //     setMappingRows(
  //       mappingRows.map(r =>
  //         r.colName === row.colName
  //           ? { ...row, type: "", dataType: "", commodity: "", unit: "", value: "", metadata: "" }
  //           : r,
  //       ),
  //     )
  //     return false
  //   }
  //   return true
  // }

  const handleUpdateJSON = async (mappingSchema: string): Promise<void> => {
    try {
      await updateJsonSchema({
        msId: Number(id),
        schema: mappingSchema,
      }).unwrap()
    } catch (error) {
      showSnackBar("Failed to update mapping schema", "error")
    }
  }

  const onGlobalEdit = async (key: string, data: string): Promise<void> => {
    if (key) {
      const newSchema = {
        ...schema,
        lastUpdateDate: new Date().toISOString(),
        [key]: data,
        columns: schema.columns.map(col => ({ ...col, [key]: key !== "dataSource" ? data : " " })),
      }
      setSchema(newSchema)
      await handleUpdateJSON(JSON.stringify(newSchema))
    }
    if (key === "rowHeader") {
      await refetchMsData()
      setHeaderRow(data)
    }
  }

  const isPartialDate = (type: string): boolean => type === "day" || type === "month" || type === "year"

  const onUpdateRow = async (row: MappingSchemaRow): Promise<void> => {
    // if (!handleValidation(row)) return
    updateMappingRow(row)
    const mappingSchema = schema
    const { columns } = mappingSchema
    const updateColumns = {}

    if (row.type === "date") {
      mappingSchema.dateFormat = row.dataType
    }

    mappingSchema.metadataFields = mappingSchema.metadataFields.filter(mf => mf.value !== row.colName)

    if (row.type === "metadata" && row.metadata !== "") {
      mappingSchema.metadataFields.push({ value: row.colName, fieldName: row.metadata })
    }

    columns.forEach((col: MappingSchemaJSONColumn) => {
      if (col.price !== row.colName) {
        updateColumns[col.commodity] = col
      }
      if (col.price === row.colName && typeof row.commodity === "number" && col.commodity !== row.commodity) {
        updateColumns[row.commodity] = col
      }
    })

    Object.keys(updateColumns).forEach(key => {
      Object.keys(updateColumns[key]).forEach(prop => {
        if (updateColumns[key][prop] === row.colName) {
          updateColumns[key][prop] = ""
        }
      })

      if (typeof row.commodity !== "number") {
        updateColumns[key] = {
          ...updateColumns[key],
          [row.type]: isPartialDate(row.dataType) ? updateColumns[key].date : row.colName,
        }
      } else {
        updateColumns[row.commodity] = {
          ...updateColumns[row.commodity],
          date: updateColumns[key].date,
          market: updateColumns[key].market,
          priceType: updateColumns[key].priceType,
          currency: updateColumns[key].currency,
          dataSource: updateColumns[key].dataSource,
          [row.type]: isPartialDate(row.dataType) ? updateColumns[key].date : row.colName,
          commodity: row.commodity,
          unit: row.unit === -1 ? row.colName : row.unit,
        }
      }

      if (updateColumns[key].date === "") {
        mappingSchema.dateFormat = ""
      }

      if (isPartialDate(row.dataType)) {
        const [date, dateFormat] = buildPartialDate(updateColumns[key].date, row.dataType, row.colName)
        updateColumns[key].date = date
        mappingSchema.dateFormat = dateFormat
      }

      if (key === "" && typeof row.commodity === "number") {
        delete updateColumns[key]
      }
    })

    await handleUpdateJSON(
      JSON.stringify({
        ...mappingSchema,
        lastUpdateDate: new Date().toISOString(),
        columns: Object.values(updateColumns),
      }),
    )
    setSchema({ ...mappingSchema, columns: Object.values(updateColumns) })
    setMappingRows(mappingRows.map(r => (r.colName === row.colName ? { ...row, isValid: true } : r)))
  }

  const onDeleteRow = async (row: MappingSchemaRow): Promise<void> => {
    const mappingSchema = schema
    const { columns } = mappingSchema
    const updateColumns = {}

    mappingSchema.metadataFields = mappingSchema.metadataFields.filter(mf => mf.value !== row.colName)

    columns.forEach((col: MappingSchemaJSONColumn) => {
      if ((columns.length > 1 && col.price !== row.colName) || columns.length === 1) {
        updateColumns[col.commodity] = col
      }
    })

    Object.keys(updateColumns).forEach(key => {
      Object.keys(updateColumns[key]).forEach(prop => {
        if (updateColumns[key].date.includes(row.colName)) {
          const [date, dateFormat] = buildPartialDate(updateColumns[key].date, row.dataType, row.colName)
          updateColumns[key].date = date
          mappingSchema.dateFormat = dateFormat
        }

        if (updateColumns[key][prop] === row.colName) {
          updateColumns[key][prop] = ""
        }
      })

      if (updateColumns[key].date === "") {
        mappingSchema.dateFormat = ""
      }

      if (updateColumns[key].price === "") {
        updateColumns[key].commodity =
          typeof updateColumns[key].commodity === "number" ? "" : updateColumns[key].commodity
        updateColumns[key].unit = typeof updateColumns[key].commodity === "number" ? "" : updateColumns[key].unit
      }
    })

    await handleUpdateJSON(
      JSON.stringify({
        ...mappingSchema,
        lastUpdateDate: new Date().toISOString(),
        columns: Object.values(updateColumns),
      }),
    )

    setSchema({ ...mappingSchema, columns: Object.values(updateColumns) })
  }

  const onDiscardRow = async (row: MappingSchemaRow): Promise<void> => {
    await handleUpdateJSON(
      JSON.stringify({
        ...schema,
        lastUpdateDate: new Date().toISOString(),
        discardedRows: [...discardedCols, row.colName],
      }),
    )

    setDiscardedCols([...discardedCols, row.colName])
  }

  const onRestoreRow = async (colName: string): Promise<void> => {
    const updatedDiscarded = discardedCols.filter(row => row !== colName)

    await handleUpdateJSON(
      JSON.stringify({
        ...schema,
        lastUpdateDate: new Date().toISOString(),
        discardedRows: updatedDiscarded,
      }),
    )

    setDiscardedCols(updatedDiscarded)
  }

  const editMappingRow = (row: MappingSchemaRow): void => {
    if (!row.type) {
      onDeleteRow(row)
    }
    setMappingRows(mappingRows.map(r => (r.colName === row.colName ? { ...row, isValid: false } : r)))
  }

  useEffect(() => {
    if (excelJson) {
      setSheetData([...jsonToRowData(excelJson)])
    }
  }, [excelJson])

  useEffect(() => {
    if (msData?.mappingSchemaJSON) {
      const mappingSchema = JSON.parse(msData.mappingSchemaJSON)
      setDiscardedCols(mappingSchema?.discardedRows ?? [])
      setSchema(s => ({
        ...s,
        ...mappingSchema,
        columns: mappingSchema?.columns?.length ? mappingSchema.columns : [initialColumn],
      }))
      setHeaderRow(mappingSchema.rowHeader)
    }
  }, [msData])

  useEffect(() => {
    if (singleRow && msData?.mappingSchemaJSON) {
      const result = singleRow?.row.map(row => ({
        ...row,
        dataType: "",
        type: "",
        ...getDataType(row.colName, JSON.parse(msData?.mappingSchemaJSON)),
      }))
      setMappingRows(result)
      setSchema(s => ({ ...s, headerHash: singleRow.hash }))
    }
  }, [singleRow])

  // useEffect(() => {
  //   if (seriesCombinations?.items.length) {
  //     const uniqueCommodities = seriesCombinations.items.filter(
  //       (item, index, current) => current.findIndex(el => el.commodityID === item.commodityID) === index,
  //     )
  //     const uniqueMarkets = seriesCombinations.items.filter(
  //       (item, index, current) => current.findIndex(el => el.marketID === item.marketID) === index,
  //     )
  //     const uniqueCurrencies = seriesCombinations.items.filter(
  //       (item, index, current) => current.findIndex(el => el.currencyID === item.currencyID) === index,
  //     )
  //     setCombinations({ commodity: uniqueCommodities, market: uniqueMarkets, currency: uniqueCurrencies })
  //   }
  // }, [seriesCombinations])

  return (
    <MappingPriceContext.Provider
      value={{
        loadingMapping: loadingMsData || loadingSingleRow || loadingExcel,
        sheetData,
        discardedCols,
        seriesCombinations: seriesCombinations?.items ?? [],
        adm0Code: msData?.adm0Code ?? 0,
        fileName: msData?.mappingSchemaFileUsed ?? "",
        mappingRows,
        errorModal,
        schema,
        onGlobalEdit,
        setDiscardedCols,
        onUpdateRow,
        onDeleteRow,
        onDiscardRow,
        onRestoreRow,
        editMappingRow,
        resetErrorModal,
      }}
    >
      {children}
    </MappingPriceContext.Provider>
  )
}

export default MappingPriceProvider
